import { Permission } from '@/common/Auth';
import { User } from '@/common/Users';
import router from '@/router';
import { AsyncNotifications } from '@/services/AsyncNotifications';
import Brand from '@/services/Brand';
import * as Sentry from '@sentry/browser';
import { Auth as AmplifyAuth } from 'aws-amplify';
import axios from 'axios';
import store from '../store';

// setTimeout placeholder
window.authTimeout = null;
window.websocket = null;

type PsuedoPermssion = 'ACCOUNT_ADMIN' | 'IS_AGENT' | 'LOGGED_IN';

class Auth {
	public checkPermission(permission: Permission | PsuedoPermssion): boolean {
		const coreUser: User = store.state.Auth.coreUser;
		const user: User = store.state.Auth.user;
		const loggedIn = window.isLoggedIn;
		const brand = Brand.getBrand();
		const userPolicies = coreUser?.policies?.[brand] ?? [];
		const isAgent = user?.isAgent === 'true' || user?.isAgent === true;
		const manuallyAddedPolicies = userPolicies.filter(p => p.manually_added === true) || [];

		if (!user) {
			return false;
		}

		if (permission === 'ACCOUNT_ADMIN') {
			return (
				(user.isOrgAdmin === 'true' || user.isOrgAdmin === true) &&
				(user.isAgent === 'true' || user.isAgent === true)
			);
		}

		if (permission === 'IS_AGENT') {
			return isAgent;
		}

		if (permission === 'LOGGED_IN') {
			return loggedIn;
		}

		const permissionsArray = JSON.parse(user.permission) as Permission[];
		const permissionsSet = new Set<Permission>(permissionsArray);

		if (store.getters['Policy/policies'].length > 0) {
			// Add Canceled Policy Permission Removal Checks here.
			const policyDocPolicies = store.getters['Policy/thirtyDayPolicies'];
			if (policyDocPolicies.length < 1) {
				permissionsSet.delete('VIEW_REPORTS');
				permissionsSet.delete('VIEW_POLICY_INFO');
				permissionsSet.delete('VIEW_COI');
			}

			if (isAgent && manuallyAddedPolicies.length > 0) {
				permissionsSet.add('VIEW_REPORTS');
			}

			const billPayPolicies = store.getters['Policy/activePolicies'];
			if (billPayPolicies.length < 1) {
				permissionsSet.delete('BILL_PAY');
				permissionsSet.delete('PAYMENT_HISTORY');
				permissionsSet.delete('VIEW_LATEST_NEWS');
				permissionsSet.delete('VIEW_RISK_MANAGEMENT');
			}
		}

		return permissionsSet.has(permission);
	}

	public isBrokerageSite(): boolean {
		if (store.state.Auth.brokerageId && store.state.Auth.isBrokerage) {
			return true;
		}
		return false;
	}

	public permissionsList() {
		const user = store.state.Auth.user;

		if (user == null) {
			return false;
		}

		const permissions = JSON.parse(user.permission);

		if (
			(user.isOrgAdmin === 'true' || user.isOrgAdmin === true) &&
			(user.isAgent === 'true' || user.isAgent === true)
		) {
			permissions.push('ACCOUNT_ADMIN');
		}

		return permissions;
	}

	public async resumeSession() {
		try {
			const currentSession = await AmplifyAuth.currentSession();
			const jwtToken = currentSession.getIdToken().getJwtToken();
			const parseIdToken = jwtToken.match(/^(.+)\.(.+)\.(.+)$/)[2];
			const decodeIdToken = JSON.parse(window.atob(parseIdToken));

			store.dispatch('Auth/setUser', decodeIdToken);
			if (decodeIdToken.brokerageId) {
				store.dispatch('Auth/setBrokerageId', decodeIdToken.brokerageId);
			}
			if (decodeIdToken.isBrokerage) {
				store.dispatch('Auth/setIsBrokerage', decodeIdToken.isBrokerage);
			}
			window.isLoggedIn = true;

			AsyncNotifications.singleton.connect(jwtToken);
		} catch (e) {
			throw Error(e);
		}
	}

	public isAuthExpired() {
		// Check if token is valid first, if not, resume session
		const now = new Date().getTime() / 1000;
		let delta = 0;

		try {
			const user = store.state.Auth.user;
			if (!('exp' in user)) {
				return false;
			}

			delta = user.exp - now;
		} catch (e) {
			Sentry.captureException(e);
			delta = 0;
		}

		if (delta <= 60 || isNaN(delta)) {
			return true;
		} else {
			return false;
		}
	}

	public async acquireTransferredSession(code) {
		// Check the incoming argument.
		if (!code || typeof code !== 'string') {
			throw new TypeError(`Invalid 'code' argument: '${code}'`);
		}

		// Retrieve the session from the service.
		// @ts-ignore
		const response = await axios({
			method: 'DELETE',
			url: `${process.env.VUE_APP_CORE_SESSIONS_URI}/sessions`,
			headers: {
				'x-code': code,
			},
			skipAuth: true,
		});
		const session = response.data;

		// Ensure the session conforms to expectations.
		const requirements = [
			{
				check: !!session,
				errorMessage: 'The session transfer response was empty',
			},
			{
				check: !!session.accessToken,
				errorMessage: 'The session transfer response does not contain a valid access token',
			},
			{
				check: !!session.idToken,
				errorMessage: 'The session transfer response does not contain a valid ID token',
			},
			{
				check: !!session.refreshToken,
				errorMessage:
					'The session transfer response does not contain a valid refresh token',
			},
			{
				check: !!session.redirectUri,
				errorMessage: 'The session transfer response does not contain a valid redirect URI',
			},
		];
		for (const requirement of requirements) {
			if (!requirement.check) {
				throw new Error(requirement.errorMessage);
			}
		}

		// Send the new authentication data to the store.
		const authData = {
			accessToken: session.accessToken,
			idToken: session.idToken,
			refreshToken: session.refreshToken,
		};

		const parseIdToken = authData.idToken.match(/^(.+)\.(.+)\.(.+)$/)[2];
		const decodeIdToken = JSON.parse(window.atob(parseIdToken));

		localStorage.setItem(
			`CognitoIdentityServiceProvider.${Brand.getClientID()}.${decodeIdToken.sub}.idToken`,
			authData.idToken,
		);
		localStorage.setItem(
			`CognitoIdentityServiceProvider.${Brand.getClientID()}.${
				decodeIdToken.sub
			}.accessToken`,
			authData.accessToken,
		);
		localStorage.setItem(
			`CognitoIdentityServiceProvider.${Brand.getClientID()}.${
				decodeIdToken.sub
			}.refreshToken`,
			authData.refreshToken,
		);
		localStorage.setItem(
			`CognitoIdentityServiceProvider.${Brand.getClientID()}.LastAuthUser`,
			decodeIdToken.sub,
		);

		await store.dispatch('Auth/authenticated');

		return session;
	}

	public async setSessionTransfer(redirectUri) {
		const userData = store.state.Auth.user;

		const session: any = {
			accessToken: localStorage.getItem(
				`CognitoIdentityServiceProvider.${Brand.getClientID()}.${userData.sub}.accessToken`,
			),
			idToken: localStorage.getItem(
				`CognitoIdentityServiceProvider.${Brand.getClientID()}.${userData.sub}.idToken`,
			),
			refreshToken: localStorage.getItem(
				`CognitoIdentityServiceProvider.${Brand.getClientID()}.${
					userData.sub
				}.refreshToken`,
			),
			redirectUri,
		};

		// Send the session data.
		const response = await axios({
			method: 'POST',
			url: `${process.env.VUE_APP_CORE_SESSIONS_URI}/sessions`,
			data: session,
		});

		if (response.status === 202) {
			const location = response.headers.location;
			window.location.assign(location);
		}
	}

	public redirectToUrl() {
		if (window.localStorage.getItem('previousUrl') !== '') {
			const parsedRedirectUrl = new URL(window.localStorage.getItem('previousUrl'));
			window.localStorage.removeItem('previousUrl');
			router.replace(parsedRedirectUrl.pathname);
			return Promise.resolve();
		}

		return Promise.reject();
	}

	public phoneNumberCleanUp(phoneNumber) {
		/**
		 * Remove - from phone number
		 */
		if (phoneNumber.includes('-')) {
			phoneNumber = phoneNumber.replace(/-/gi, '');
		}

		/**
		 * If phone number is longer than 10 characters, grab the last 10
		 */
		if (phoneNumber.length > 10) {
			const phoneNumberLength = phoneNumber.length;

			phoneNumber = phoneNumber.substring(phoneNumberLength - 10, phoneNumberLength);
		}

		return phoneNumber;
	}
}

export default new Auth();

function generateVerification() {
	let verification = '';
	const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	for (let i = 0; i < 32; i++) {
		verification += possible.charAt(Math.floor(Math.random() * possible.length));
	}
	return verification;
}
